import { Box, Typography } from "@mui/material";

import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import type { SuitLevelV2Inventory } from "@/global/store/data/types";
import type { InventorySuiteParkingPackageInfoType } from "@/types";

export const InventoryCardListItem = (props: {
  label: string;
  value: string;
}) => {
  const { sm: smDown } = useCssMediaQuery("down");
  return (
    <Box
      display={"flex"}
      alignItems={"center"}
      justifyContent={"space-between"}
      gap={"0.5rem"}
    >
      <Typography
        variant="body1"
        color="text.secondary"
        fontSize={smDown ? "0.75rem" : "0.9rem"}
        fontWeight={"500"}
      >
        {props?.label}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        fontSize={smDown ? "0.75rem" : "0.9rem"}
        fontWeight={"500"}
      >
        {props?.value}
      </Typography>
    </Box>
  );
};

export const InventoryCardPerks = (props: {
  inventory: SuitLevelV2Inventory;
}) => {
  const inventory = props.inventory;
  const teamConfig = useTeamConfig();
  const inventoryCardSettings = teamConfig?.uiConfigs?.inventoryCardSettings;

  function calcInventoryCardSettings() {
    // per-event override applied to all suites takes precedence
    const eventOverride =
      inventoryCardSettings?.FBPackageValueByEventCode?.[inventory.eventCode];
    if (eventOverride) {
      return eventOverride;
    }

    // handling inventory suite package info as string
    if (typeof inventoryCardSettings?.inventorySuitePackageInfo === "string") {
      return inventoryCardSettings.inventorySuitePackageInfo;
    }

    // handling inventory suite package info as array
    const inventorySuitePackageInfo = (
      inventoryCardSettings?.inventorySuitePackageInfo as InventorySuiteParkingPackageInfoType[]
    )?.find((itm) => itm.suiteId === (inventory.suiteName || inventory.id));

    // prettier-ignore
    if (inventorySuitePackageInfo) {
      // check if overrideEventCode array exists and has items
      if (inventorySuitePackageInfo.overrideEventCode && inventorySuitePackageInfo.overrideEventCode.length > 0) {
        const hasOverrideEventCode = inventorySuitePackageInfo.overrideEventCode.includes(inventory.eventCode);

        if (hasOverrideEventCode && inventorySuitePackageInfo.overrideFBPackageValue) {
          return inventorySuitePackageInfo.overrideFBPackageValue;
        }
      }

      // return default FBPackageValue if no override applies
      return inventorySuitePackageInfo.FBPackageValue;
    }

    // default return
    return "Included";
  }

  return (
    <>
      {/** handling perks in inventory api */}
      {inventory?.perks?.map((perk: any, idx: number) => {
        if (perk.label === null) return null;
        return (
          <InventoryCardListItem
            key={idx}
            label={perk.label}
            value={perk.seatCount || "Not Included"}
          />
        );
      })}

      {inventoryCardSettings?.isPBLineItemsVisible ? (
        <>
          {inventoryCardSettings?.isParkingPassesVisible &&
          inventory?.parkingSeatCount === 0 ? (
            <InventoryCardListItem
              label="Parking Passes"
              value="Not Included"
            />
          ) : null}

          {inventoryCardSettings?.isParkingSeatCountVisible &&
          inventory?.parkingSeatCount > 0 ? (
            <InventoryCardListItem
              label="Parking Passes: "
              value={String(inventory?.parkingSeatCount)}
            />
          ) : null}

          {inventoryCardSettings?.isParkingPriceVisible &&
          Number(inventory?.parkingSeatCount) > 0 ? (
            <InventoryCardListItem
              label={"Parking Price: "}
              value={getUSCurrency(Number(inventory?.parkingSeatCount) * 15)}
            />
          ) : null}
        </>
      ) : null}

      {!inventoryCardSettings?.hideFBPackageLineItem ? (
        <InventoryCardListItem
          label={inventoryCardSettings?.FBLabel || "F&B Package: "}
          value={calcInventoryCardSettings()}
        />
      ) : null}
    </>
  );
};
